import { service } from '@ember/service';

import type { CreateRecordProperties, Service } from 'webapp-types';
import { AlertEventCondition, ModelName } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import type { MetricSetup } from 'webapp/models-custom';
import Alert from 'webapp/models-custom/alert';
import type AlertRecord from 'webapp/models/alert-setting';

import ModelManagerForStoreService from 'webapp/services/model-manager/for-store';

export type TrackingOriginForCreateOrEdit =
	| 'notifications'
	| 'metric_insights'
	| 'query-builder-list'
	| 'scripts-list'
	| 'metrics'
	| 'library';

const defaultTrackingOriginForCreateOrEdit: TrackingOriginForCreateOrEdit = 'notifications';

export default class ModelManagerAlertsService extends ModelManagerForStoreService<
	AlertRecord,
	Alert
> {
	@service('request') declare requestService: Service.Request;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('constants') declare constantsService: Service.Constants;
	@service('account') declare accountService: Service.Account;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('model-manager/notifications')
	declare mmNotificationsService: Service.ModelManager.Notifications;

	getAllModels() {
		return this.createModelsFromRecordsArray(this.savedRecords);
	}

	createModelForMetricSetup(
		metricSetup: MetricSetup,
		origin: TrackingOriginForCreateOrEdit = defaultTrackingOriginForCreateOrEdit,
	) {
		this.analyticsService.event(TrackingEvent.NEW_ALERT, { origin });
		return this.createNewModel({
			metricSetupKey: metricSetup.key!,
			metricSetupSpaceAccessId: metricSetup.spaceAccessId,
			metricSetupDimensions: metricSetup.dimensions,
			metricSetupFilters: metricSetup.filtersData,
		});
	}

	edit(
		model: Alert,
		origin: TrackingOriginForCreateOrEdit = defaultTrackingOriginForCreateOrEdit,
		isFromCreateChoicePopupFlow = false,
	) {
		this.mmNotificationsService.openEditPopup(model, origin, isFromCreateChoicePopupFlow);
	}

	createAndEdit(origin: TrackingOriginForCreateOrEdit = defaultTrackingOriginForCreateOrEdit) {
		const model = this.createNewModel();
		this.analyticsService.event(TrackingEvent.NEW_ALERT, { origin });
		this.edit(model, origin);
	}

	createAndEditForMetricSetup(
		metricSetup: MetricSetup,
		origin: TrackingOriginForCreateOrEdit = defaultTrackingOriginForCreateOrEdit,
		isFromCreateChoicePopupFlow = false,
	) {
		const model = this.createModelForMetricSetup(metricSetup, origin);
		this.edit(model, origin, isFromCreateChoicePopupFlow);
	}

	getDefaultTemplateForCondition(condition?: AlertEventCondition) {
		let defaultTemplate;
		if (typeof condition === 'number') {
			defaultTemplate = this.constantsService.alertTemplateLayouts[condition];
		}
		if (!defaultTemplate) {
			defaultTemplate = '::metric:: from ::datasource:: ::condition:: ::value:: ::period::.';
		}
		return defaultTemplate;
	}

	createDuplicatedModel(model: Alert) {
		const data = model.getDataForClone();
		return this.createNewModel(data);
	}

	delete(alert: Alert, afterDelete?: () => void) {
		if (this.accountService.handleDemo()) {
			return;
		}

		const isAlertMine = alert.isMine,
			affirmativeFunc = () => {
				this.infolayerService.ok(
					isAlertMine ? 'Alert deleted!' : 'Unsubscribed from the alert!',
				);
				alert
					.delete()
					.then(() => {
						if (afterDelete) {
							afterDelete();
						}
					})
					.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
			};

		this.popupService.openConfirmPopup({
			onConfirm: affirmativeFunc,
			title: `Are you sure you want to ${isAlertMine ? 'delete' : 'unsubscribe from'} the alert "${alert.name}"?`,
			text: '',
			confirmAction: isAlertMine ? 'Delete' : 'Unsubscribe',
			cancelAction: 'Cancel',
		});
	}

	toggleIsActive(model: Alert) {
		model.isActive = !model.isActive;
		void model.saveAndCatch();
	}

	// ------ Requests ------

	_alertsRequested = false;
	_alertsRequestPromise: Promise<void> | null = null;
	requestAlerts(): Promise<void> {
		const variablePrefix = 'alerts';
		return this.requestService.dataRequestOnce<typeof variablePrefix>(
			this,
			variablePrefix,
			() => {
				return this.requestService.storeFindAll(this.modelName);
			},
		);
	}

	// ------ For-store ------

	modelName = ModelName.ALERT_SETTING;
	override modelTitle = 'alert';

	createModel(model: AlertRecord): Alert {
		return new Alert({ model });
	}

	override createNewRecord(properties: CreateRecordProperties<AlertRecord> = {}): AlertRecord {
		if (!properties.template) {
			properties.template = this.getDefaultTemplateForCondition();
		}
		return super.createNewRecord(properties);
	}
}

declare module '@ember/service' {
	interface Registry {
		'model-manager/alerts': ModelManagerAlertsService;
	}
}
